import { IFileReceipt } from '@engineering11/files-web'
import { AtLeast, NamedMapping, Optional } from '@engineering11/types'
import { startCase } from 'lodash'
import { ICandidateComplianceResponses } from './candidate-compliance.model'
import { EmploymentAuthorizationType, IWorkAuthorizationExpiration } from './candidate.model'
import { APPLICATION_STATE, MilitaryAffiliation } from './enum'
import {
  IAddress,
  ICertification,
  IEducation,
  IFirstImpression,
  IMatchedRequirableCertification,
  IMatchedRequirableSkill,
  IReference,
  IRequirableCertification,
  IRequirableSkill,
  ISkill,
  IWorkHistory,
} from './interfaces'
import { ILanguage } from './language.model'
import { IPortfolio } from './portfolio.model'
import { ISocialMedia } from './social-media.enum'

export interface ICandidateSource {
  source: string
  integration: string
  integrationId: string
}

/** The candidate's record of the application */
export interface ICandidateJobApplication {
  id: string
  candidateId: string // maybe change to userId
  jobPostId: string // all posting information needed for analysis
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  summary?: string
  address?: IAddress
  photoURL?: string | null // Consider renaming to thumbnailURL, avatar, or profileURL
  website?: string | null
  currentJobTitle?: string
  firstImpression?: IFirstImpression | null
  skills: ISkill[]
  certifications: ICertification[]
  education: IEducation[]
  workHistory: IWorkHistory[]
  portfolio: IPortfolio[]
  references: IReference[]
  militaryAffiliation: MilitaryAffiliation[]
  candidateCoreCopy?: NamedMapping<number | undefined>
  candidateResponses?: ICandidateResponses
  candidateComplianceResponses?: ICandidateComplianceResponses
  resumeId?: string
  parsedResumeFileId?: string
  candidateSource?: ICandidateSource
  languages: ILanguage[]
  socialMedia?: ISocialMedia
  resumeAttachment?: IFileReceipt
}

export type ICandidateApplicationDetails = Pick<
  ICandidateJobApplication,
  'phoneNumber' | 'email' | 'summary' | 'address' | 'website' | 'firstName' | 'lastName' | 'resumeAttachment'
>

export const applicationFieldMappings = new Map<keyof ICandidateJobApplication, string>()
  .set('firstImpression', 'First Impression')
  .set('skills', 'Skills')
  .set('certifications', 'Certifications')
  .set('education', 'Education')
  .set('workHistory', 'Work History')
  .set('languages', 'Languages')
  .set('portfolio', 'Portfolio')
  .set('references', 'References')
  .set('socialMedia', 'Social Media')
  .set('militaryAffiliation', 'Military Affiliations')
  .set('resumeAttachment', 'Resume')

/**
 * Responses the candidate gives immediately prior to submitting an application.
 */
export interface ICandidateResponses {
  // referralSource: string
  // referralDetail?: string // Only available if the referralSource is 'other' or 'Employee Referral'
  yearsRelevantExperience: number
  monthsRelevantExperience: number
  visaSponsorshipRequired: boolean
  legalWorkAuthorization: boolean
  legalWorkAuthorizationType?: EmploymentAuthorizationType | null
  legalWorkAuthorizationCountryCode?: string | null
  tempWorkAuthorizationExpiration?: IWorkAuthorizationExpiration
  relocationConfirmed?: boolean
  hoursConfirmed?: boolean // Only applies if job has set hours
  reassignmentConfirmed?: boolean
  travelConfirmed?: boolean // Only applies if job requires travel
  isDefaultApplication?: boolean
}

export const fieldsRequiredForPreview = ['jobPostId', 'skills', 'certifications', 'education'] as const
export type FieldsRequiredForPreview = (typeof fieldsRequiredForPreview)[number]

// The data needed to submit an application
export type ICandidateJobSubmission = Omit<ICandidateJobApplication, 'id' | 'candidateId' | 'candidateCoreCopy'>

// The data needed to request an application preview
export type ICandidateJobSubmissionPreview = AtLeast<ICandidateJobSubmission, FieldsRequiredForPreview>

export interface ICandidateJobApplicationStash extends Pick<ICandidateJobApplication, 'jobPostId' | 'candidateId' | 'candidateResponses'> {
  id?: string
  selectedWorkHistoryIds?: string[]
  selectedEducationIds?: string[]
  selectedReferenceIds?: string[]
  selectedFirstImpressionId?: string | null
}

export interface IJobApplicationStage extends Optional<ICandidateJobApplication, 'candidateResponses'> {}

export interface IMatchedJobApplication extends ICandidateJobApplication {
  skills: IMatchedRequirableSkill[]
  missingSkills: IRequirableSkill[]
  certifications: IMatchedRequirableCertification[]
  missingCertifications: IRequirableCertification[]
}

export interface IJobApplicationScores {
  coreMatchScore: number | null
  jobMatchScore: number
}

export enum NextStep {
  CultureIndex = 'culture-index',
  RequestedUpdate = 'requested-updates',
}

export interface IProcessedJobApplicationV2 extends IProcessedJobApplication {
  application: IProcessedJobApplication
  nextSteps: NextStep[]
}

export interface IProcessedJobApplication extends IMatchedJobApplication, IJobApplicationScores {
  appliedDate: Date
  applicationState: APPLICATION_STATE
  requestedUpdated?: IRequestedUpdate[]
  conversationId?: string
  previousApplicationState?: APPLICATION_STATE
  reassignment?: IApplicationReassignment
}

export interface IApplicationReassignment {
  status: ReassignmentStatus
  reassignedAt: Date
  reassignedFrom?: IApplicationSource
  /** Targets when this application was copied to one or more other jobs (source application). */
  reassignedTo?: IApplicationSource[]
  reassignedByUserId?: string
  isViewedByCandidate?: boolean
}

export interface IApplicationSource {
  applicationId: string
  jobPostId: string
}

export enum ReassignmentStatus {
  ARCHIVED_ORIGINAL = 'ARCHIVED_ORIGINAL',
  ACTIVE_REASSIGNMENT = 'ACTIVE_REASSIGNMENT',
  COPIED_ORIGINAL = 'COPIED_ORIGINAL',
}

/** The employer's record of the candidate's application */
export interface IJobPostApplication extends Omit<IProcessedJobApplication, 'candidateComplianceResponses'> {
  starRating?: number
  pendingVirtualDialogue?: boolean
  pendingVirtualDialogueCount?: number
  completedVirtualDialogueCount?: number
  hasNotes?: boolean
  hasFeedback?: boolean
  totalApplicationShares?: number
  applicationFiles?: IFileReceipt[]
  requestedActions?: IRequestedActions[]
}
export enum RequestedAction {
  CultureIndex = 'cultureIndex',
}
export const requestedUpdateFields = [
  'skills',
  'certifications',
  'education',
  'references',
  'workHistory',
  'languages',
  'portfolio',
  'firstImpression',
  'socialMedia',
  'militaryAffiliation',
  'resumeAttachment',
] as const
export type RequestedUpdateFields = (typeof requestedUpdateFields)[number]

export interface IRequestedUpdate {
  message: string
  fulfilled: boolean
  requestedFieldChanged: keyof Pick<IProcessedJobApplication, RequestedUpdateFields>
  timestamp: Date
  submitter: string
  createdByAutomation?: boolean
}
export interface IRequestedActions {
  fulfilled: boolean
  requestedAction: RequestedAction
  timestamp: Date
  submitter: string
  createdByAutomation?: boolean
}

export const requestedUpdateFieldToLabel: Record<RequestedUpdateFields, string> = requestedUpdateFields.reduce(
  (acc, curr) => ({ ...acc, [curr]: startCase(curr) }),
  {} as Record<RequestedUpdateFields, string>
)
